/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { Suspense, lazy, useState } from 'react'
import api from '../../utils/api'
import LoadingPage from '../../pages/LoadingPage'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Box, Stack } from '@mui/material'
import { type SlotEmailSettings } from '../../models/SlotEmailSettings'
import { useFieldArray, useForm, type SubmitHandler, FormProvider, Controller } from 'react-hook-form'
import { notification } from '../../components/Notifications'
import EmailSettingsCard from './components/EmailSettingsCard'
import Tab from '@mui/material/Tab'
import TabPanel from '@mui/lab/TabPanel'
import TabList from '@mui/lab/TabList'
import TabContext from '@mui/lab/TabContext'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'

export interface SlotEmailSettingsFeatureProps {
  slotId: string
  handleCloseModal?: () => void
}

const SlotEmailSettingsFeature: React.FC<SlotEmailSettingsFeatureProps> = ({
  slotId, handleCloseModal = () => {}
}): JSX.Element => {
  const [tab, setTab] = useState('1')

  const methods = useForm<FormData>({
    defaultValues: {
      settings: []
    }
  })

  const { reset, handleSubmit, control, formState: { isDirty } } = methods

  const { data: settings, isLoading, error } = useQuery({
    enabled: !isDirty,
    queryKey: ['slot_email_settings', slotId],
    queryFn: async (): Promise<SlotEmailSettings[]> => {
      const result = await api.get(`/api/distribution_series/${slotId}/email_settings/`)
      reset({ settings: result.data })
      return result.data
    }
  })

  interface FormData {
    settings?: SlotEmailSettings[]
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'settings'
  })

  const { mutateAsync, isPending: isUpdating } = useMutation({
    mutationFn: async (data: SlotEmailSettings[]): Promise<any> => {
      const result = await api.patch(`/api/distribution_series/${slotId}/email_settings/`, data)
      return result.data
    }
  })

  const onSubmit: SubmitHandler<FormData> = async (data, e) => {
    try {
      if (data.settings) {
        await mutateAsync(data.settings)
        notification('Successfully updated email settings!', { variant: 'success' })
        handleCloseModal()
      }
    } catch (error) {
      notification('Failed to update email settings!', { variant: 'error' })
    }
    e?.preventDefault()
  }

  if (isLoading) {
    return <LoadingPage />
  }
  return (
    <Suspense fallback={<LoadingPage />}>
    <>
      {error && <Alert severity="error">an issue occoured</Alert>}

      <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>

      <TabContext value={tab}>
        <TabList onChange={(e, v: string) => { setTab(v) }}>
          <Tab label="Student" value="1"/>
          <Tab label="Instructor" value="2"/>
          <Tab label="Admin" value="3"/>
        </TabList>

        <TabPanel value="1" sx={{ paddingTop: 4 }}>
          <Stack>
          {fields?.map((field, index) => {
            if (field.userType !== 'STUDENT') {
              return <></>
            }
            return <EmailSettingsCard key={field.id} name={`settings.${index}`} setting={field} control={control} />
          })}
          </Stack>
        </TabPanel>
        <TabPanel value="2" sx={{ paddingTop: 4 }}>
          <Stack>
            {fields?.map((field, index) => {
              if (field.userType !== 'INSTRUCTOR') {
                return <></>
              }
              return <EmailSettingsCard key={field.id} name={`settings.${index}`} setting={field} control={control} />
            })}
          </Stack>
        </TabPanel>
        <TabPanel value="3" sx={{ paddingTop: 4 }}>
          <Stack>
          {fields?.map((field, index) => {
            if (field.userType !== 'ADMIN') {
              return <></>
            }
            return <EmailSettingsCard key={field.id} name={`settings.${index}`} setting={field} control={control} />
          })}
          </Stack>
        </TabPanel>
      </TabContext>

      <DefaultModalActions
        destructiveButtonProps={{
          onClick: handleCloseModal
        }}
        confirmButtonProps={{
          text: 'Save',
          type: 'submit',
          loading: isUpdating,
          disabled: !isDirty
        }}
      />
      </form>
      </FormProvider>
    </>
    </Suspense>
  )
}

export default SlotEmailSettingsFeature
